import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-smooth ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-hero-gradient text-white shadow-elegant hover:shadow-glow transform hover:scale-105 transition-smooth font-semibold",
        soft: "bg-soft-cream text-earth-brown shadow-card hover:bg-warm-beige transition-smooth",
        "outline-warm": "border-2 border-primary/20 bg-background/50 text-primary shadow-card hover:bg-primary/5 hover:border-primary/40 backdrop-blur-sm",
        "standard-gradient": "bg-standard-gradient text-white shadow-elegant hover:shadow-glow transform hover:scale-105 transition-smooth font-semibold",
        "premium-gradient": "bg-premium-gradient text-white shadow-elegant hover:shadow-glow transform hover:scale-105 transition-smooth font-semibold",
        "voyage-gradient": "bg-voyage-gradient text-white shadow-elegant hover:shadow-glow transform hover:scale-105 transition-smooth font-semibold",
        "mariage-gradient": "bg-mariage-gradient text-white shadow-elegant hover:shadow-glow transform hover:scale-105 transition-smooth font-semibold",
        "naissance-gradient": "bg-naissance-gradient text-white shadow-elegant hover:shadow-glow transform hover:scale-105 transition-smooth font-semibold",
        "rainbow": "bg-rainbow-gradient text-white shadow-elegant hover:shadow-glow transform hover:scale-105 transition-smooth font-semibold",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-12 rounded-lg px-10 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
